import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import ClientLayout from '../../components/ClientLayout';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

// --- PALETA DE ALTO CONTRASTE ---
const THEME = {
    primary: '#1B4F1B',      // Verde Bosque Profundo
    secondary: '#A07040',    // Café/Bronce Fuerte
    accent: '#D4AF37',       // Dorado Metálico
    dark: '#111111',         // Casi Negro (Texto Principal)
    text: '#374151',         // Gris Oscuro (Texto Secundario)
    white: '#FFFFFF',
    bg: '#F3F4F6',           // Fondo Pagina
    grid: '#E5E7EB'          // Líneas de guía
};

const PIE_COLORS = ['#1B4F1B', '#D4AF37', '#A07040', '#8D230F', '#2C3E50', '#5D4037'];

function AnalisisNegocio() {
    const [loading, setLoading] = useState(true);
    // CAMBIO 1: Reemplazamos 'totalGasto' por 'productoTop'
    const [kpis, setKpis] = useState({ totalKg: 0, productoTop: 'N/A', promedioPedido: 0 });
    const [chartData, setChartData] = useState([]);
    const [pieData, setPieData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            try {
                const { data } = await api.get('/pedidos?limit=100', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const pedidos = data.data || []; 
                procesarDatos(pedidos);
            } catch (error) {
                console.error("Error cargando análisis", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const procesarDatos = (pedidos) => {
        const validos = pedidos.filter(p => p.estado !== 'CANCELADO');

        // 1. KPIS BÁSICOS
        let totalKg = 0;
        validos.forEach(p => {
            const kg = Number(p.cantidadGrano) + Number(p.cantidadMolido);
            totalKg += kg;
        });

        // 2. TENDENCIA MENSUAL (Gráficas)
        const porMes = {};
        const pedidosOrdenados = [...validos].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        pedidosOrdenados.forEach(p => {
            const fecha = new Date(p.createdAt);
            const key = `${fecha.toLocaleString('es-MX', { month: 'short' }).toUpperCase()} ${fecha.getFullYear().toString().slice(-2)}`;
            
            if (!porMes[key]) porMes[key] = { name: key, kilos: 0, gasto: 0 };
            
            porMes[key].kilos += (Number(p.cantidadGrano) + Number(p.cantidadMolido));
            porMes[key].gasto += Number(p.montoTotal);
        });
        setChartData(Object.values(porMes));

        // 3. MIX DE PRODUCTOS & PRODUCTO TOP
        const porProducto = {};
        validos.forEach(p => {
            const key = p.tipoProducto;
            if (!porProducto[key]) porProducto[key] = 0;
            porProducto[key] += (Number(p.cantidadGrano) + Number(p.cantidadMolido));
        });

        // Convertir a array para gráfica y buscar el TOP
        let maxKg = 0;
        let topName = 'Sin Datos';

        const pieArray = Object.keys(porProducto).map(key => {
            const val = porProducto[key];
            const name = key.replace(/_/g, ' ');
            
            // Lógica para encontrar el favorito
            if (val > maxKg) {
                maxKg = val;
                topName = name; // Guardamos el nombre "bonito"
            }

            return { name, value: val };
        });

        setPieData(pieArray);

        // ACTUALIZAR ESTADO DE KPIS
        setKpis({
            totalKg: totalKg.toFixed(1),
            productoTop: topName, // Nuevo KPI
            promedioPedido: validos.length > 0 ? (totalKg / validos.length).toFixed(1) : 0
        });
    };

    const formatCurrency = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(val);

    // --- ESTILOS ---
    const cardStyle = { 
        background: THEME.white, 
        padding: 25, 
        borderRadius: 12, 
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', 
        border: `1px solid ${THEME.grid}`,
        flex: 1, 
        minWidth: 200 
    };

    const titleStyle = { 
        color: THEME.dark, 
        fontSize: 18, 
        fontWeight: 800, 
        marginBottom: 20, 
        borderBottom: `2px solid ${THEME.primary}`,
        paddingBottom: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    };

    const kpiLabelStyle = {
        fontSize: 13, 
        fontWeight: 'bold', 
        color: THEME.text, 
        textTransform: 'uppercase', 
        marginBottom: 5
    };

    const kpiValueStyle = {
        fontSize: 36, 
        fontWeight: 900, 
        color: THEME.dark
    };

    if (loading) return <div style={{textAlign:'center', marginTop:50, fontWeight:'bold'}}>Cargando métricas...</div>;

    return (
        <ClientLayout title="Inteligencia de Negocio">
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                
                {/* 1. SECCIÓN DE KPIS (Modificada) */}
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 30 }}>
                    
                    {/* KPI 1: Consumo Total */}
                    <div style={cardStyle}>
                        <div style={kpiLabelStyle}>Consumo Histórico</div>
                        <div style={kpiValueStyle}>{kpis.totalKg} <span style={{fontSize:18, color: THEME.secondary}}>Kg</span></div>
                    </div>

                    {/* KPI 2: CAMBIO AQUI -> PRODUCTO ESTRELLA */}
                    <div style={cardStyle}>
                        <div style={kpiLabelStyle}>Variedad Favorita</div>
                        {/* Ajustamos tamaño de fuente para que quepa el texto */}
                        <div style={{...kpiValueStyle, color: THEME.primary, fontSize: 24, lineHeight: 1.2}}>
                            {kpis.productoTop}
                        </div>
                    </div>

                    {/* KPI 3: Promedio */}
                    <div style={cardStyle}>
                        <div style={kpiLabelStyle}>Ticket Promedio</div>
                        <div style={kpiValueStyle}>{kpis.promedioPedido} <span style={{fontSize:18, color: THEME.secondary}}>Kg</span></div>
                    </div>
                </div>

                {/* 2. GRÁFICA PRINCIPAL */}
                <div style={{ ...cardStyle, padding: 30, marginBottom: 30 }}>
                    <div style={titleStyle}>📈 Tendencia de Consumo</div>
                    <div style={{ height: 350, width: '100%' }}>
                        {chartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorKilos" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={THEME.primary} stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor={THEME.primary} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={THEME.grid} />
                                    <XAxis 
                                        dataKey="name" 
                                        stroke={THEME.dark} 
                                        fontWeight="bold" 
                                        tick={{fill: THEME.dark}} 
                                        axisLine={false}
                                        tickLine={false}
                                        dy={10}
                                    />
                                    <YAxis 
                                        stroke={THEME.dark} 
                                        fontWeight="bold" 
                                        tick={{fill: THEME.dark}} 
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip 
                                        contentStyle={{ background: THEME.dark, color: '#fff', borderRadius: 8, border: 'none', fontWeight: 'bold' }}
                                        itemStyle={{ color: '#fff' }}
                                        formatter={(value) => [`${value.toFixed(1)} Kg`, 'Consumo']}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="kilos" 
                                        stroke={THEME.primary} 
                                        strokeWidth={3} 
                                        fillOpacity={1} 
                                        fill="url(#colorKilos)" 
                                        activeDot={{ r: 6, strokeWidth: 0 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontStyle: 'italic' }}>
                                Insuficientes datos para graficar tendencias.
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. GRÁFICAS SECUNDARIAS */}
                <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap' }}>
                    
                    {/* PASTEL: PREFERENCIAS */}
                    <div style={{ ...cardStyle, flex: '1 1 400px' }}>
                        <div style={titleStyle}>☕ Preferencia de Variedades</div>
                        <div style={{ height: 320, width: '100%' }}>
                            {pieData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={70} 
                                            outerRadius={100}
                                            paddingAngle={4}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            contentStyle={{ background: THEME.dark, borderRadius: 8, border: 'none', color: '#fff', fontWeight: 'bold' }}
                                            formatter={(value) => [`${value.toFixed(1)} Kg`, 'Total']} 
                                        />
                                        <Legend 
                                            verticalAlign="bottom" 
                                            height={36} 
                                            iconType="circle"
                                            wrapperStyle={{ fontWeight: '600', color: THEME.dark, paddingTop: '20px' }} 
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                                    Sin datos de variedades.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* BARRAS: GASTO MENSUAL */}
                    <div style={{ ...cardStyle, flex: '1 1 400px' }}>
                        <div style={titleStyle}>💰 Inversión Mensual</div>
                        <div style={{ height: 320, width: '100%' }}>
                            {chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={THEME.grid} />
                                        <XAxis 
                                            dataKey="name" 
                                            stroke={THEME.dark} 
                                            fontWeight="bold" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            dy={10} 
                                        />
                                        <YAxis hide /> 
                                        <Tooltip 
                                            cursor={{fill: 'rgba(0,0,0,0.05)'}}
                                            contentStyle={{ background: THEME.dark, borderRadius: 8, border: 'none', color: '#fff', fontWeight: 'bold' }}
                                            formatter={(value) => [formatCurrency(value), 'Gasto']}
                                        />
                                        <Bar 
                                            dataKey="gasto" 
                                            fill={THEME.secondary} 
                                            radius={[6, 6, 0, 0]} 
                                            barSize={45} 
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                                    Sin datos financieros.
                                </div>
                            )}
                        </div>
                    </div>

                </div>

            </div>
        </ClientLayout>
    );
}

export default AnalisisNegocio;